import { supabase } from "@/platform/lib/supabase";
import type { SignInValues, SignUpValues, OnboardingValues } from "./schemas";

export async function signInWithPassword(values: SignInValues) {
  const { data, error } = await supabase.auth.signInWithPassword(values);
  if (error) throw error;
  return data;
}

export async function updateProfile(
  userId: string,
  patch: Partial<{
    full_name: string;
    display_name: string;
    exam_date: string;
    weekly_hours: number;
    consent_research: boolean;
  }>,
): Promise<void> {
  const { error } = await supabase
    .from("einstein_profiles")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", userId);
  if (error) throw error;
}

export async function deleteAccount(): Promise<void> {
  // Supabase Auth não permite delete pelo cliente (precisa service_role).
  // Estratégia: sinaliza intenção via update, depois cron/admin processa.
  // Por enquanto, signOut + email pro suporte.
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function signUpWithPassword(
  values: SignUpValues,
  options?: { intentTenant?: "einstein" | "usp" },
) {
  const { full_name, email, password } = values;
  const intentTenant = options?.intentTenant ?? "einstein";
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name,
        display_name: full_name.split(" ")[0] ?? full_name,
        intent_tenant: intentTenant,
      },
      emailRedirectTo: `${window.location.origin}/auth/callback?tenant=${intentTenant}`,
    },
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Verifica o código OTP de 6 dígitos enviado por email no signup.
 * Estabelece sessão se válido.
 *
 * Pré-requisito: template "Confirm signup" no Supabase deve usar
 * `{{ .Token }}` (código) em vez de `{{ .ConfirmationURL }}` (link).
 */
export async function verifySignupOtp(email: string, token: string) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: "email",
  });
  if (error) throw error;
  return data;
}

/**
 * Reenvia o código OTP de signup.
 */
export async function resendSignupOtp(email: string) {
  const { error } = await supabase.auth.resend({ type: "signup", email });
  if (error) throw error;
}

export async function signInWithGoogle(options?: { intentTenant?: "einstein" | "usp" }) {
  const intentTenant = options?.intentTenant ?? "einstein";
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback?tenant=${intentTenant}`,
    },
  });
  if (error) throw error;
  return data;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
  });
  if (error) throw error;
}

export async function updatePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function completeOnboarding(userId: string, values: OnboardingValues): Promise<void> {
  // consent_terms_at NÃO é gravado aqui — o trigger einstein_handle_new_user
  // já grava no momento do signup (que é onde a copy "Ao criar conta, você
  // aceita..." é exibida e o ato afirmativo de aceite acontece).
  const { error } = await supabase
    .from("einstein_profiles")
    .update({
      exam_date: values.exam_date,
      weekly_hours: values.weekly_hours,
      consent_research: values.consent_research,
      onboarding_completed: true,
    })
    .eq("id", userId);
  if (error) throw error;
}
